import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  SelectChangeEvent,
  CircularProgress,
  TextField,
  InputAdornment,
  styled
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { fetchLocations, Place, getLocationPath, sortPlacesHierarchically } from '../../services/locationService';

interface LocationDropdownProps {
  organizationId: string;
  value: string;
  onChange: (locationId: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const LocationDropdown: React.FC<LocationDropdownProps> = ({
  organizationId,
  value,
  onChange,
  disabled = false,
  placeholder = 'No location selected (optional)'
}) => {
  const [locations, setLocations] = useState<Place[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const selectRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (organizationId) {
      loadLocations(organizationId);
    } else {
      setLocations([]);
      setExpandedNodes(new Set());
    }
  }, [organizationId]);

  const loadLocations = async (orgId: string) => {
    setLoadingLocations(true);
    try {
      const places = await fetchLocations(orgId);
      const sortedPlaces = sortPlacesHierarchically(places);
      setLocations(sortedPlaces);
      setExpandedNodes(new Set());
    } catch (error) {
      console.error('Error loading locations:', error);
      const mockLocations: Place[] = [
        { 
          id: `${orgId}-loc1`, 
          name: 'Main Office',
          hierarchy: []
        },
        { 
          id: `${orgId}-loc2`, 
          name: 'Conference Room A',
          hierarchy: []
        },
        { 
          id: `${orgId}-loc3`, 
          name: 'Training Center',
          hierarchy: []
        },
        { 
          id: `${orgId}-loc4`, 
          name: 'Warehouse',
          hierarchy: []
        },
      ];
      setLocations(mockLocations);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleLocationChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value;
    onChange(newValue);

    const selectElement = document.getElementById('location-select');
    if (selectElement) {
      selectElement.blur();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const selectableItems = flattenedTree;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex(prev => {
        const next = prev + 1;
        return next >= selectableItems.length ? prev : next;
      });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(prev => {
        const next = prev - 1;
        return next < 0 ? prev : next;
      });
    } else if (event.key === 'Enter' && highlightedIndex >= 0 && highlightedIndex < selectableItems.length) {
      event.preventDefault();
      const selected = selectableItems[highlightedIndex];
      onChange(selected.id);
      setMenuOpen(false);
      setSearchQuery('');
      setHighlightedIndex(-1);
    } else if (event.key === 'Escape') {
      setMenuOpen(false);
      setSearchQuery('');
      setHighlightedIndex(-1);
    }
  };

  // Reset highlighted index when search changes
  useEffect(() => {
    setHighlightedIndex(searchQuery ? 0 : -1);
  }, [searchQuery]);

  // Scroll to highlighted item when it changes
  useEffect(() => {
    if (highlightedIndex >= 0 && menuOpen) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        // Find the menu item by its data-index attribute
        const menuItem = document.querySelector(`[data-index="${highlightedIndex}"]`);
        if (menuItem) {
          menuItem.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          });
        }
      });
    }
  }, [highlightedIndex, menuOpen]);

  const toggleNodeExpansion = (nodeId: string) => {
    console.log('Toggling expansion for:', nodeId);
    
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const hasChildren = (location: Place): boolean => {
    if (!locations || locations.length === 0) {
      return false;
    }
    return locations.some(loc => {
      const parentId = loc.hierarchy[loc.hierarchy.length - 2]?.id;
      return parentId === location.id;
    });
  };

  // Function to highlight matching text
  const highlightText = (text: string, highlight: string) => {
    if (!highlight.trim()) {
      return text;
    }
    const regex = new RegExp(`(${highlight})`, 'gi');
    const parts = text.split(regex);

    return (
      <Box component="span">
        {parts.map((part, i) =>
          regex.test(part) ? (
            <Box
              key={i}
              component="span"
              sx={{
                backgroundColor: theme => theme.palette.mode === 'dark'
                  ? 'rgba(144, 202, 249, 0.35)'
                  : 'rgba(25, 118, 210, 0.12)',
                fontWeight: 600,
                borderRadius: '2px',
                px: 0.25
              }}
            >
              {part}
            </Box>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </Box>
    );
  };

  // Auto-expand nodes when searching to show matched results
  useEffect(() => {
    if (searchQuery && locations.length > 0) {
      const nodesToExpand = new Set<string>();

      locations.forEach(loc => {
        if (loc.name.toLowerCase().includes(searchQuery.toLowerCase())) {
          // Add all parent nodes to expand set
          loc.hierarchy.forEach((node, index) => {
            if (index < loc.hierarchy.length - 1) {
              nodesToExpand.add(node.id);
            }
          });
        }
      });

      setExpandedNodes(nodesToExpand);
    } else if (!searchQuery) {
      // Reset to collapsed state when search is cleared
      setExpandedNodes(new Set());
    }
  }, [searchQuery, locations]);

  const flattenedTree = useMemo(() => {
    if (!locations || locations.length === 0) {
      return [];
    }

    const result: Place[] = [];
    const processed = new Set<string>();

    const addNodeAndChildren = (nodeId: string) => {
      const node = locations.find(loc => loc.id === nodeId);
      if (!node || processed.has(nodeId)) return;

      // Filter by search query if present
      if (searchQuery) {
        const matchesSearch = node.name.toLowerCase().includes(searchQuery.toLowerCase());
        const hasMatchingChildren = locations.some(loc => {
          const parentId = loc.hierarchy[loc.hierarchy.length - 2]?.id;
          return parentId === nodeId && loc.name.toLowerCase().includes(searchQuery.toLowerCase());
        });

        // Skip nodes that don't match search and don't have matching children
        if (!matchesSearch && !hasMatchingChildren) {
          // Still process children to check for matches
          const children = locations.filter(loc => {
            const parentId = loc.hierarchy[loc.hierarchy.length - 2]?.id;
            return parentId === nodeId;
          });

          children
            .sort((a, b) => a.name.localeCompare(b.name))
            .forEach(child => addNodeAndChildren(child.id));
          return;
        }
      }

      processed.add(nodeId);
      result.push(node);

      if (expandedNodes.has(nodeId) || searchQuery) {
        const children = locations.filter(loc => {
          const parentId = loc.hierarchy[loc.hierarchy.length - 2]?.id;
          return parentId === nodeId;
        });

        children
          .sort((a, b) => a.name.localeCompare(b.name))
          .forEach(child => addNodeAndChildren(child.id));
      }
    };

    const roots = locations.filter(loc => loc.hierarchy.length === 1);
    roots
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(root => addNodeAndChildren(root.id));

    return result;
  }, [locations, expandedNodes, searchQuery]);

  return (
    <StyledFormControl fullWidth disabled={disabled || !organizationId || loadingLocations} ref={selectRef}>
      <Select
        id="location-select"
        value={value || ''}
        onChange={handleLocationChange}
        open={menuOpen}
        onOpen={() => {
          setMenuOpen(true);

          // If there's a selected value, expand parent nodes to show it
          if (value && locations.length > 0) {
            const selectedLocation = locations.find(loc => loc.id === value);
            if (selectedLocation) {
              const nodesToExpand = new Set<string>();
              // Add all parent nodes to expand set
              selectedLocation.hierarchy.forEach((node, index) => {
                if (index < selectedLocation.hierarchy.length - 1) {
                  nodesToExpand.add(node.id);
                }
              });
              setExpandedNodes(nodesToExpand);

              // Scroll to selected item after menu renders
              setTimeout(() => {
                const selectedElement = document.querySelector(`[value="${value}"].Mui-selected`);
                if (selectedElement) {
                  selectedElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                  });
                }
              }, 150);
            }
          }

          // Focus search input when menu opens
          setTimeout(() => searchInputRef.current?.focus(), 100);
        }}
        onClose={() => {
          setMenuOpen(false);
          setSearchQuery(''); // Clear search when closing
        }}
        displayEmpty
        renderValue={(selectedValue) => {
          if (selectedValue === '') {
            return (
              <Typography
                component="em"
                sx={{
                  color: theme => theme.palette.text.disabled,
                  fontStyle: 'italic'
                }}
              >
                {placeholder}
              </Typography>
            );
          }
          if (!locations || locations.length === 0) {
            return selectedValue;
          }
          const selectedLoc = locations.find(loc => loc.id === selectedValue);
          return selectedLoc ? getLocationPath(selectedLoc) : selectedValue;
        }}
        endAdornment={
          loadingLocations && (
            <CircularProgress size={20} sx={{ position: 'absolute', right: 30 }} />
          )
        }
        MenuProps={{
          PaperProps: {
            elevation: 0,
            style: {
              maxHeight: 300,
              zIndex: 1500, // Higher than WalkthroughGuide's z-index (1300-1400)
            },
            sx: (theme) => ({
              backgroundColor: `${theme.palette.background.paper} !important`,
              border: theme.palette.mode === 'light'
                ? '0.6px solid rgba(17, 17, 17, 0.15)'
                : `0.6px solid ${theme.palette.divider}`,
              borderRadius: '4px',
              boxShadow: theme.shadows[1],
              '&::before': {
                display: 'none',
              },
            }),
          },
          sx: {
            zIndex: 1500, // Ensure the menu itself has high z-index
          },
        }}
      >
        {/* Hidden MenuItem for the current value to prevent MUI warnings */}
        {value && locations && !locations.find(loc => loc.id === value) && (
          <MenuItem value={value} sx={{ display: 'none' }}>
            {value}
          </MenuItem>
        )}

        {!organizationId ? (
          <MenuItem value="" disabled sx={{ fontFamily: 'Open Sans', fontSize: '14px' }}>
            Select an organization first
          </MenuItem>
        ) : (
          <>
            {/* Search input */}
            <Box
              sx={{
                p: 1,
                borderBottom: theme => `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 0,
                backgroundColor: theme => theme.palette.background.paper,
                zIndex: 1,
              }}
              onMouseDown={(e) => e.preventDefault()} // Prevent menu from closing
            >
              <TextField
                inputRef={searchInputRef}
                fullWidth
                size="small"
                placeholder="Search locations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20, color: theme => theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery('')}
                        sx={{ padding: '4px' }}
                      >
                        <ClearIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: (theme) => ({
                    backgroundColor: `${theme.palette.background.default} !important`,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.action.hover,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                  })
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'Open Sans',
                    fontSize: '14px',
                  },
                }}
              />
            </Box>

            {/* Show "no location" option only when not searching */}
            {!searchQuery && (
              <MenuItem
                value=""
                onClick={() => {
                  onChange('');
                  setMenuOpen(false);
                }}
                sx={{ fontFamily: 'Open Sans', fontSize: '14px' }}
              >
                <em>{placeholder}</em>
              </MenuItem>
            )}
            {(!locations || locations.length === 0) && !loadingLocations ? (
              <MenuItem value="" disabled sx={{ fontFamily: 'Open Sans', fontSize: '14px' }}>
                No locations available
              </MenuItem>
            ) : searchQuery && flattenedTree.length === 0 ? (
              <MenuItem value="" disabled sx={{ fontFamily: 'Open Sans', fontSize: '14px', py: 2 }}>
                No locations found for "{searchQuery}"
              </MenuItem>
            ) : (
              flattenedTree.map((loc, index) => {
                const indentLevel = loc.hierarchy.length - 1;
                const iconWidth = 24;
                const levelIndent = 24;
                const baseIndent = 8 + iconWidth;
                const totalIndent = baseIndent + (indentLevel * levelIndent);
                
                const placeType = loc.hierarchy[loc.hierarchy.length - 1]?.placeTypeId || '';
                const hasChildNodes = hasChildren(loc);
                const isExpanded = expandedNodes.has(loc.id);
                const isSelected = loc.id === value;
                const isHighlighted = index === highlightedIndex;

                return (
                  <MenuItem
                    key={loc.id}
                    value={loc.id}
                    data-location-id={loc.id}
                    data-index={index}
                    selected={isSelected}
                    onClick={() => {
                      console.log('MenuItem clicked:', loc.id);
                      onChange(loc.id);
                      setMenuOpen(false);
                      setSearchQuery('');
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    sx={{
                      fontFamily: 'Open Sans',
                      fontSize: '14px',
                      paddingLeft: `${totalIndent}px`,
                      paddingRight: '8px',
                      position: 'relative',
                      backgroundColor: isHighlighted
                        ? theme => theme.palette.action.hover
                        : isSelected
                          ? theme => theme.palette.action.selected
                          : 'transparent',
                    }}
                    title={getLocationPath(loc)}
                  >
                    {hasChildNodes && (
                      <IconButton
                        size="small"
                        onMouseDown={(e) => {
                          e.preventDefault();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleNodeExpansion(loc.id);
                        }}
                        sx={{ 
                          position: 'absolute',
                          left: `${4 + (indentLevel * levelIndent)}px`,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          padding: '4px',
                          zIndex: 1,
                        }}
                      >
                        {isExpanded ? 
                          <ExpandMoreIcon sx={{ fontSize: '18px' }} /> : 
                          <ChevronRightIcon sx={{ fontSize: '18px' }} />
                        }
                      </IconButton>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Typography
                        component="div"
                        sx={{
                          fontFamily: 'Open Sans',
                          fontSize: '14px',
                          color: theme => theme.palette.text.primary,
                          fontWeight: placeType === 'SITE' || placeType === 'BUILDING' ? 600 : 400,
                        }}
                      >
                        {highlightText(loc.name, searchQuery)}
                      </Typography>
                      {placeType && (
                        <Typography
                          sx={{
                            marginLeft: 'auto',
                            fontSize: '11px',
                            color: theme => theme.palette.text.secondary,
                            fontFamily: 'Open Sans',
                          }}
                        >
                          {placeType.toLowerCase()}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                );
              })
            )}
          </>
        )}
      </Select>
    </StyledFormControl>
  );
};

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: `${theme.palette.background.default} !important`,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    height: '40px',
    '& fieldset': {
      border: 'none',
    },
    '&:hover': {
      backgroundColor: `${theme.palette.background.default} !important`,
      border: `1px solid ${theme.palette.action.hover}`,
    },
    '&.Mui-focused': {
      backgroundColor: `${theme.palette.background.default} !important`,
      border: `1px solid ${theme.palette.primary.main}`,
    },
    '&.Mui-disabled': {
      backgroundColor: `${theme.palette.background.default} !important`,
      '& .MuiSelect-select': {
        color: theme.palette.text.disabled,
      },
    },
  },
  '& .MuiSelect-select': {
    backgroundColor: `${theme.palette.background.default} !important`,
    height: '40px',
    lineHeight: '40px',
    padding: '0 32px 0 14px',
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'Open Sans',
    fontSize: '14px',
    color: theme.palette.text.primary,
  },
  '& .MuiSelect-icon': {
    right: '14px',
    color: theme.palette.text.secondary,
  },
  '& .MuiMenuItem-root': {
    fontFamily: 'Open Sans',
    fontSize: '14px',
    color: theme.palette.text.primary,
  },
}));

export default LocationDropdown;